import { useEffect, useMemo, useState } from 'react';
import {
  useCustomerSettings,
  usePutCustomerSettings,
  useSetProvision,
  useSetStandard,
  useStandardStatus,
  useStandardValidation,
  type CustomerSettingsInput,
  type DashboardDefinition,
} from '../../api/dashboards.js';

/**
 * CSAGE-583: standard dashboards, for internal admins in the dashboard's
 * Settings tab. A standard dashboard is one board served read-only to every
 * customer it is provisioned to, each seeing only its own rows; the copy per
 * customer it replaces is not needed. Here an admin marks the board standard
 * (the server refuses a board that would leak across customers), provisions
 * it to customers, and edits one customer's view of it: its own title,
 * hidden widgets and filters, optional elements it turns on, and refresh.
 * Who inside a customer sees it is Manage access, per customer.
 */
export function StandardPanel({
  dashboardId,
  definition,
}: {
  dashboardId: string;
  definition: DashboardDefinition | null;
}): JSX.Element {
  const status = useStandardStatus(dashboardId, true);
  const validation = useStandardValidation(dashboardId, true);
  const setStandard = useSetStandard(dashboardId);
  const setProvision = useSetProvision(dashboardId);
  const [newCustomer, setNewCustomer] = useState('');
  const [customer, setCustomer] = useState<string | null>(null);

  const s = status.data;
  const errors = validation.data?.errors ?? [];
  const failure = (setStandard.error ?? setProvision.error) as Error | null;

  return (
    <section className="stack" aria-labelledby="standard-panel-title">
      <div className="section-head">
        <div className="section-head-title">
          <h2 id="standard-panel-title">Standard dashboard</h2>
          <p>
            One board for every customer it is provisioned to, each seeing only its own data,
            read-only. Replaces a copy per customer.
          </p>
        </div>
        {s && (
          <button
            type="button"
            className={s.standard ? 'btn btn--secondary btn--sm' : 'btn btn--primary btn--sm'}
            disabled={setStandard.isPending || (!s.standard && errors.length > 0)}
            onClick={() => setStandard.mutate(!s.standard)}
          >
            {s.standard ? 'Unmark standard' : 'Mark standard'}
          </button>
        )}
      </div>

      {status.isLoading && (
        <p className="text-dim" role="status">
          Loading…
        </p>
      )}
      {failure && (
        <div className="alert alert--danger" role="alert">
          {failure.message}
        </div>
      )}
      {s && !s.standard && errors.length > 0 && (
        <div className="alert alert--warning" role="status">
          <strong>Can&apos;t be served to many customers yet:</strong>
          <ul>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {s?.standard && (
        <>
          <div className="field">
            <span className="field-label">Provisioned to</span>
            {s.provisions.length === 0 ? (
              <p className="text-dim">No customer yet. Nobody outside Canopy sees it.</p>
            ) : (
              <ul className="manage-access-list">
                {s.provisions.map((c) => (
                  <li key={c} className="manage-access-row">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      aria-pressed={customer === c}
                      onClick={() => setCustomer(c)}
                    >
                      {c}
                      {s.customersWithSettings.includes(c) ? ' · own settings' : ''}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      aria-label={`Withdraw from ${c}`}
                      disabled={setProvision.isPending}
                      onClick={() => {
                        if (customer === c) setCustomer(null);
                        setProvision.mutate({ customer: c, provisioned: false });
                      }}
                    >
                      Withdraw
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form
              className="standard-provision-form"
              onSubmit={(e) => {
                e.preventDefault();
                const c = newCustomer.trim();
                if (!c) return;
                setProvision.mutate(
                  { customer: c, provisioned: true },
                  { onSuccess: () => setNewCustomer('') },
                );
              }}
            >
              <input
                className="input"
                aria-label="Customer to provision"
                placeholder="Customer, e.g. Telaid"
                value={newCustomer}
                maxLength={128}
                onChange={(e) => setNewCustomer(e.target.value)}
              />
              <button
                type="submit"
                className="btn btn--secondary btn--sm"
                disabled={!newCustomer.trim() || setProvision.isPending}
              >
                Provision
              </button>
            </form>
          </div>

          {customer && (
            <CustomerSettingsEditor
              key={customer}
              dashboardId={dashboardId}
              customer={customer}
              definition={definition}
            />
          )}
        </>
      )}
    </section>
  );
}

interface Choice {
  id: string;
  label: string;
}

/** One customer's view of the board. Every field is saved together and replaces what is stored. */
function CustomerSettingsEditor({
  dashboardId,
  customer,
  definition,
}: {
  dashboardId: string;
  customer: string;
  definition: DashboardDefinition | null;
}): JSX.Element {
  const q = useCustomerSettings(dashboardId, customer);
  const put = usePutCustomerSettings(dashboardId, customer);
  const [draft, setDraft] = useState<CustomerSettingsInput | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (!q.data) return;
    const s = q.data.settings;
    setDraft({
      title: s?.title ?? null,
      description: s?.description ?? null,
      hiddenWidgetIds: s?.hiddenWidgetIds ?? [],
      hiddenFilterIds: s?.hiddenFilterIds ?? [],
      shownOptionalIds: s?.shownOptionalIds ?? [],
      columnOverrides: s?.columnOverrides ?? {},
      filterDefaults: s?.filterDefaults ?? {},
      refreshIntervalSec: s?.refreshIntervalSec ?? null,
    });
  }, [q.data]);

  const { widgets, filters, optional } = useMemo(() => choicesFor(definition), [definition]);

  if (q.isLoading || !draft) {
    return (
      <p className="text-dim" role="status">
        Loading {customer}&apos;s settings…
      </p>
    );
  }

  const toggle = (key: 'hiddenWidgetIds' | 'hiddenFilterIds' | 'shownOptionalIds', id: string) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            [key]: d[key].includes(id) ? d[key].filter((x) => x !== id) : [...d[key], id],
          }
        : d,
    );

  return (
    <div className="card stack" aria-label={`${customer}'s view`}>
      <div className="section-head">
        <div className="section-head-title">
          <h3>{customer}&apos;s view</h3>
          <p>Leave a field empty to use the dashboard&apos;s own.</p>
        </div>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={put.isPending}
          onClick={() => put.mutate(draft, { onSuccess: (r) => setWarnings(r.warnings) })}
        >
          {put.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {put.error && (
        <div className="alert alert--danger" role="alert">
          {(put.error as Error).message}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="alert alert--warning" role="status">
          <ul>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid-2">
        <label className="field">
          <span className="field-label">Title</span>
          <input
            className="input"
            value={draft.title ?? ''}
            maxLength={200}
            onChange={(e) =>
              setDraft({ ...draft, title: e.target.value.trim() ? e.target.value : null })
            }
          />
        </label>
        <label className="field">
          <span className="field-label">Refresh interval (sec)</span>
          <input
            type="number"
            min={0}
            max={3600}
            className="input"
            value={draft.refreshIntervalSec ?? ''}
            onChange={(e) => {
              // Number(), not parseInt: "1e5" must not quietly become 1.
              const n = Number(e.target.value);
              setDraft({
                ...draft,
                refreshIntervalSec: e.target.value === '' || !Number.isInteger(n) ? null : n,
              });
            }}
          />
        </label>
      </div>
      <label className="field">
        <span className="field-label">Description</span>
        <textarea
          className="input"
          rows={2}
          maxLength={2000}
          value={draft.description ?? ''}
          onChange={(e) =>
            setDraft({ ...draft, description: e.target.value.trim() ? e.target.value : null })
          }
        />
      </label>
      <ChoiceGroup
        legend="Hide widgets"
        choices={widgets}
        checked={draft.hiddenWidgetIds}
        onToggle={(id) => toggle('hiddenWidgetIds', id)}
      />
      <ChoiceGroup
        legend="Hide filters"
        choices={filters}
        checked={draft.hiddenFilterIds}
        onToggle={(id) => toggle('hiddenFilterIds', id)}
      />
      <ChoiceGroup
        legend="Turn on optional widgets, filters and columns"
        choices={optional}
        checked={draft.shownOptionalIds}
        onToggle={(id) => toggle('shownOptionalIds', id)}
        empty="This dashboard has nothing marked optional."
      />
    </div>
  );
}

function ChoiceGroup({
  legend,
  choices,
  checked,
  onToggle,
  empty,
}: {
  legend: string;
  choices: Choice[];
  checked: string[];
  onToggle(id: string): void;
  empty?: string;
}): JSX.Element {
  return (
    <fieldset className="field">
      <legend className="field-label">{legend}</legend>
      {choices.length === 0 ? (
        <p className="text-dim">{empty ?? 'None.'}</p>
      ) : (
        choices.map((c) => (
          <label key={c.id} className="standard-choice">
            <input
              type="checkbox"
              checked={checked.includes(c.id)}
              onChange={() => onToggle(c.id)}
            />
            {c.label}
          </label>
        ))
      )}
    </fieldset>
  );
}

/** The widgets, filters and optional elements of a widgets definition, by id. */
function choicesFor(definition: DashboardDefinition | null): {
  widgets: Choice[];
  filters: Choice[];
  optional: Choice[];
} {
  const layout = definition?.layout;
  if (!layout || layout.kind !== 'widgets') return { widgets: [], filters: [], optional: [] };
  const optional: Choice[] = [];
  for (const w of layout.widgets) {
    if (w.optional) optional.push({ id: w.id, label: `Widget: ${w.title}` });
    if (w.type === 'table') {
      for (const c of w.config.columns) {
        if (c.optional) {
          optional.push({ id: `${w.id}.${c.field}`, label: `Column: ${w.title} · ${c.label}` });
        }
      }
    }
  }
  for (const f of layout.filters) {
    if (f.optional) optional.push({ id: f.id, label: `Filter: ${f.label}` });
  }
  return {
    widgets: layout.widgets.filter((w) => !w.optional).map((w) => ({ id: w.id, label: w.title })),
    filters: layout.filters.filter((f) => !f.optional).map((f) => ({ id: f.id, label: f.label })),
    optional,
  };
}
